import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import {
  add, canvasOf, fade, fill, place, playerColor,
} from '../fx/common';
import { PLATE, duelistName, duelistPlate } from '../fx/duelistPlate';
import { DAMAGE_SLASH, lifeNumberGlyphWidth, makeLifeNumber } from '../fx/lifeNumber';
import { ornamentSpec } from '../hud/ornaments';

/**
 * **決着（K.O. → 勝者の名乗り）**（Issue #604 v3。指示書 `../direction/win.md`。キービジュアルは無い＝方向は人が決めた）。
 *
 * ```
 * 0      止める: 盤の側（背景・盤＝実写・置かれたカード）が白黒に（`grade.boardGrayscale`。HUD と演出の層は色のまま）。
 *        白い硬い閃光 2 フレーム・小さい揺れ
 * 100    画面の中央に巨大な「K.O.」（銀の金属文字＋黒の縁、後ろに赤の斬り傷。damage の数字と同じ作り）を 3 倍から叩きつけ（130）
 *        → ヒットストップ 120ms（硬く・色収差）→ 重い揺れ・硬い火花
 * 700    敗者の顔の枠と LP のゲージに硬い線のひびが走る（HUD より手前の層。HUD が小さく震える）
 * 900    砕ける: HUD の部品を `appear: 0` で消し、同じ場所・同じ絵（装飾の画像・顔の絵・ゲージの窓の地）の破片が
 *        回りながら重力で落ちて画面の下へ消える（部品の形の閃光 2 フレーム・火花・揺れ）。K.O. は細く潰れて消える
 * 1600   盤の側の色が戻る（2000 で戻りきる）。勝者の HUD は残り、敗者の側は空のまま
 * 2000   勝者の側から start と同じ斜めに切った鋼の板（`../fx/duelistPlate.js`。デッキのイラスト・アイコン・名前・デッキ名）が
 *        加速して中央へ → 2300 急停止（前の斜めの辺の閃光・火花・ヒットストップ 100ms・揺れ）
 * 2470   板の上に **「WINNER 名前!」の 1 行の見出し**（銀の金属文字＋黒の縁。名前の後ろにだけその人の色の硬い斬り傷の差し）を
 *        2.6 倍から叩きつけ（2500）→ 止め 80ms（閃光 1 フレーム・両端から硬い火花）→ 揺れ → 小さく弾んで止まる。
 *        字の幅は画面の 86%（板・盤の縁よりはっきり広い。v2 ユーザー 2026-10-01: 盤の縁の幅にそろって窮屈だった）。
 *        板の下の名前の札は出さない（v3 ユーザー 2026-10-01: 名前は見出しに入る）
 * 3200   見得（わずかな震えだけ）
 * 4300   板と見出しが縦に細く潰れて消える → 4380〜4480 敗者の HUD が `appear` で戻る（帯の後も動画が続くため）。何も残さない
 * ```
 *
 * 敗者の HUD の箱は HUD の口（`plate` / `life`）と装飾の縦横比（`ornamentSpec`）から出す（`drawHud.js::layoutP1` と同じ式）。
 * 勝者が 0 / 1 でない（引き分けなど）ときは K.O. だけ（砕く側・名乗る側が無い）。
 */
const FLASH = 34;
const KO_IN = 100;
const KO_HIT = 130;
const KO_STOP = 120;        // ヒットストップ
const CRACK = 700;
const BREAK = 900;
const KO_OUT = [BREAK, BREAK + 60];
const FALL_FADE = [BREAK + 600, BREAK + 900];
const GREY_BACK = [1600, 2000];
const PLATE_IN = 2000;
const PLATE_HIT = 2300;
const PLATE_STOP = 100;
const WIN_IN = 2470;
const WIN_HIT = 2500;
const WIN_STOP = 80;
const OUT = [4300, 4420];
const HUD_BACK = [4380, 4480];
const D = 4500;

const WIN_SIZE = 220;       // 見出しの文字（テクスチャの大きさ。画面の幅に合わせて縮める）
const WIN_FIT = 0.86;       // 見出しの字の幅 ÷ 画面の幅
const WIN_Y = 400;          // 見出しの中心（板の上。紋章・フェイズ名とぶつけない）
const { W: PW, H: PH, SKEW } = PLATE;
const PLATE_Y = 775;        // 板の中心（見出しの下。札が無いぶん下へ寄せて見出しとの間を空ける）
const LINES = 10;
const KO_SIZE = 560;        // K.O. の文字（字の高さが画面の 4 割ほど）
const KO_SPARKS = 26;
const RAYS = 6;
const RINGS = 2;
const BREAK_SPARKS = 22;
const WIN_SPARKS = 28;

/** 金属文字のテクスチャを group に置く（中心 x、順番 order） */
function putQuad(ctx, g, tex, x, order) {
  const m = ctx.kit.quad(ctx.kit.plain(tex.texture, 1), tex.width, tex.height);
  m.position.set(x, 0, 0);
  m.renderOrder = order;
  g.add(m);
  return m;
}

/** 勝者（0 / 1。それ以外は null） */
const winnerOf = (payload) => {
  const w = payload?.winner ?? payload?.player;
  return w === 0 || w === 1 ? w : null;
};

/** 敗者の HUD の顔の枠とゲージの箱（舞台のピクセル。`drawHud.js::layoutP1` と同じ式を HUD の口から解く） */
function loserBoxes(ctx, p) {
  const plate = ctx.stage?.hud?.plate?.(p);
  const life = ctx.stage?.hud?.life?.(p);
  if (!plate || !life) return null;
  const right = p === 1;
  const ph = plate.h;
  const pw = Math.round(ph * (ornamentSpec('portraitFrame')?.aspect ?? 1.29));
  const portrait = { x: right ? plate.x + plate.w - pw : plate.x, y: plate.y, w: pw, h: ph };
  const bw = plate.w - pw * 0.72;
  const bh = Math.round(bw / (ornamentSpec('barFrame')?.aspect ?? 6.2));
  const bar = { x: right ? plate.x : plate.x + pw * 0.72, y: life.y + 2 - bh, w: bw, h: bh };
  return { portrait, bar, right };
}

/** 多角形の窓（割合）の外接（割合） */
const winRect = (win) => {
  if (!win) return { l: 0, r: 1, t: 0, b: 1 };
  const xs = win.map((q) => q[0]); const ys = win.map((q) => q[1]);
  return { l: Math.min(...xs), r: Math.max(...xs), t: Math.min(...ys), b: Math.max(...ys) };
};

export default defineEffect({
  situation: 'win',
  role: 'effect',
  label: '決着（K.O. → 敗者の HUD が砕ける → 勝者の名乗り）',
  color: '#ffd35a',
  duration: () => D,
  clears: () => [{ mark: 'attackArrow' }, { mark: 'battlePanel' }],
  scene: [
    {
      ms: 0,
      grade: {
        // 盤の側だけ白黒（HUD・演出の層は色のまま）→ 1500〜1800 で戻る
        boardGrayscale: { keys: [{ ms: 0, value: 1 }, { ms: GREY_BACK[0], value: 1, ease: 'ease_in_out' }, { ms: GREY_BACK[1], value: 0 }] },
        // 止めの瞬間とヒットストップの間は硬く。色収差は K.O. の叩きつけの瞬間だけ
        contrast: {
          keys: [
            { ms: 0, value: 1.5, ease: 'hold' }, { ms: 60, value: 1, ease: 'hold' },
            { ms: KO_HIT, value: 1.4, ease: 'hold' }, { ms: KO_HIT + KO_STOP, value: 1, ease: 'hold' },
            { ms: PLATE_HIT, value: 1.25, ease: 'hold' }, { ms: PLATE_HIT + PLATE_STOP, value: 1, ease: 'hold' },
            { ms: WIN_HIT, value: 1.2, ease: 'hold' }, { ms: WIN_HIT + WIN_STOP, value: 1 },
          ],
        },
        chroma: { keys: [{ ms: 0, value: 0, ease: 'hold' }, { ms: KO_HIT, value: 0.014, ease: 'ease_out' }, { ms: KO_HIT + KO_STOP + 200, value: 0 }] },
      },
    },
    { ms: 0, shake: { amp: 14, freq: 26, decayMs: 160 } },
    { ms: KO_HIT + KO_STOP, shake: { amp: 56, freq: 20, decayMs: 560 } },
    { ms: BREAK, shake: { amp: 26, freq: 24, decayMs: 360 } },
    { ms: PLATE_HIT + PLATE_STOP, shake: { amp: 34, freq: 22, decayMs: 420 } },
    { ms: WIN_HIT + WIN_STOP, shake: { amp: 22, freq: 24, decayMs: 300 } },
  ],
  sounds: ({ payload }) => [
    { at: 0, name: 'attack', gain: 0.9 },
    { at: KO_HIT - 15, name: 'turn_change', gain: 0.95 },
    ...(winnerOf(payload) == null ? [] : [
      { at: CRACK, name: 'destoroy', gain: 0.5 },
      { at: BREAK - 10, name: 'destoroy', gain: 1 },
      { at: PLATE_IN, name: 'phase_change', gain: 0.6 },
      { at: PLATE_HIT - 15, name: 'attack', gain: 0.9 },
      { at: WIN_HIT - 15, name: 'turn_change', gain: 0.85 },
    ]),
  ],

  /*
   * **敗者の HUD**: ひびの間は小さく震え、砕けた瞬間に消える（`appear: 0`。破片は演出の層が同じ場所から作る）。
   * 帯の終わりに `appear` で戻す（顔の枠は角から叩き込まれ、ゲージは外側から伸びる。出方は `drawHud.js::appearStyle`）
   */
  hud: ({ localMs: t, payload }) => {
    const w = winnerOf(payload);
    if (w == null) return [];
    const loser = 1 - w;
    const back = tween(t, HUD_BACK[0], HUD_BACK[1], 'power3_in');
    const appear = t < BREAK ? 1 : back;
    const tremble = t >= CRACK && t < BREAK ? 0.25 + 0.5 * seg(t, CRACK, BREAK) : 0;
    return [
      { anchor: 'plate', player: loser, appear, shake: tremble },
      { anchor: 'life', player: loser, appear, shake: tremble },
      { anchor: 'hand', player: loser, appear },
    ];
  },

  layers: [
    {
      id: 'ko',
      label: '止め・K.O. の叩きつけ',
      space: 'screen',
      script: (ctx) => {
        const { THREE, kit } = ctx;
        const { w: W, h: H } = canvasOf(ctx);
        const root = new THREE.Group();
        const cx = W / 2; const cy = H * 0.5;
        const rand = seeded(`${ctx.seed}:win:ko`);
        const groups = {};
        const layer = (n) => { if (!groups[n]) groups[n] = add(root, new THREE.Group(), n); return groups[n]; };

        const flash = add(layer(12), place(fill(ctx, '#ffffff', W, H, { blend: 'add' }), cx, cy), 12);

        // K.O.（damage の数字と同じ作り: 銀の金属文字＋黒の縁、後ろに赤の斬り傷）
        const tex = kit.metalTextTexture('K.O.', { size: KO_SIZE, edge: null });
        const ko = makeLifeNumber(ctx, root, tex, { order: 10, colors: DAMAGE_SLASH, name: 'ko' });
        const koFit = Math.min(1, (W * 0.92) / Math.max(1, tex.width));

        // 叩きつけの硬い火花（K.O. の外周から外へ）
        const sparkCols = ['#ffffff', '#ffd54a', '#ffb020', '#fff2b0'];
        const sparks = Array.from({ length: KO_SPARKS }, (_, i) => {
          const m = add(layer(9), fill(ctx, sparkCols[i % sparkCols.length], 1, 1), 9);
          const a = (i / KO_SPARKS) * Math.PI * 2 + (rand() - 0.5) * 0.4;
          return { m, a, r0: 260 + rand() * 160, len: 140 + rand() * 220, w: 3 + rand() * 4, speed: 1100 + rand() * 1400 };
        });

        return {
          root,
          update: ({ localMs: t }) => {
            fade(flash, t < FLASH ? 0.55 : (t >= KO_HIT && t < KO_HIT + FLASH ? 0.18 : 0));

            // 3 倍から叩きつけ → 止め → 小さく弾んで止まる → 砕け始めで細く潰れて消える
            const slam = tween(t, KO_IN, KO_HIT, 'power2_in');
            const settle = t >= KO_HIT + KO_STOP ? 1 + 0.05 * Math.sin(seg(t, KO_HIT + KO_STOP, KO_HIT + KO_STOP + 180) * Math.PI) : 1;
            const sq = tween(t, KO_OUT[0], KO_OUT[1], 'power2_in');
            const s = (3 - 2 * slam) * settle * koFit;
            const jitter = t >= KO_HIT && t < KO_HIT + KO_STOP ? Math.sin(t / 7) * 4 : 0;
            place(ko.g, cx + jitter, cy, { sx: s * (1 + 0.2 * sq), sy: s * Math.max(0.001, 1 - sq) });
            ko.lay(t < KO_HIT ? 0.35 : 0.35 + 0.65 * tween(t, KO_HIT, KO_HIT + 70, 'expo_out'));
            fade(ko.g, t >= KO_IN && t < KO_OUT[1] ? seg(t, KO_IN, KO_IN + 15) : 0);

            // 火花（止めの間はその場で止まる）
            const u = t < KO_HIT + KO_STOP ? 0 : (t - KO_HIT - KO_STOP) / 1000;
            sparks.forEach((sp) => {
              const k = Math.min(1, u / 0.4);
              const r = sp.r0 + sp.speed * u * (1 - 0.4 * k);
              const len = sp.len * (1 - k);
              place(sp.m, cx + Math.cos(sp.a) * (r + len / 2), cy + Math.sin(sp.a) * (r + len / 2) * 0.6, { sx: Math.max(0.001, len), sy: sp.w, rot: sp.a });
              fade(sp.m, t >= KO_HIT && k < 1 ? 1 : 0);
            });
          },
        };
      },
    },
    {
      id: 'shatter',
      label: '敗者の HUD が砕ける（ひび → 破片）',
      space: 'screen',
      front: true,
      script: (ctx) => {
        const { THREE, kit } = ctx;
        const root = new THREE.Group();
        const w = winnerOf(ctx.payload);
        if (w == null) return { root };
        const loser = 1 - w;
        const boxes = loserBoxes(ctx, loser);
        if (!boxes) return { root };
        const { right } = boxes;
        const rand = seeded(`${ctx.seed}:win:shatter`);
        const portraitImg = ctx.cards?.portrait?.(loser) ?? null;
        const pieces = [];

        /*
         * 1 つの部品（顔の枠 / ゲージ）。中心に置いた group（P2 は左右反転）の中で、装飾の画像の破片と、
         * 窓の中身（顔の絵 / ゲージの地）の破片を割る。ひびは装飾の破片の線
         */
        const part = (name, box, inner, order, rays = RAYS) => {
          const g = add(root, new THREE.Group(), order);
          g.position.set(box.x + box.w / 2, box.y + box.h / 2, 0);
          g.scale.set(right ? -1 : 1, 1, 1);
          const spec = ornamentSpec(name);
          const wr = winRect(spec?.window);
          const iw = (wr.r - wr.l) * box.w; const ih = (wr.b - wr.t) * box.h;
          const ix = (wr.l + wr.r - 1) / 2 * box.w; const iy = (wr.t + wr.b - 1) / 2 * box.h;
          const shardsOf = (grp, z) => grp.children.map((m) => {
            const sp = 220 + rand() * 380;
            return {
              m, z, ...m.userData,
              vx: m.userData.dir[0] * sp * 0.6 + (rand() - 0.5) * 160,
              vy: -120 - rand() * 260 + m.userData.dir[1] * sp * 0.3,
              spin: (rand() - 0.5) * 14,
              ox: 0, oy: 0,
            };
          });
          // 窓の中身（顔の絵 / ゲージの地）。絵は HUD と同じ cover で、P2 も左右反転しない
          const innerGrp = kit.crackShards(inner.tex ?? null, iw, ih, `${ctx.seed}:win:${name}:in`, {
            rays, rings: 1,
            material: (tx) => { const m = kit.plain(tx, 1); if (!tx) m.color?.set?.(inner.color); return m; },
          });
          if (inner.tex && inner.aspect) {
            const a = inner.aspect;
            const imgW = box.w / box.h > a ? box.w : box.h * a;
            const imgH = imgW / a;
            innerGrp.children.forEach((m) => {
              const pos = m.geometry.attributes.position; const uv = m.geometry.attributes.uv;
              for (let i = 0; i < pos.count; i += 1) {
                const xl = pos.getX(i) + m.position.x + ix; const yl = pos.getY(i) + m.position.y + iy;
                uv.setXY(i, 0.5 + (right ? -xl : xl) / imgW, 0.5 + yl / imgH);
              }
              uv.needsUpdate = true;
            });
          }
          innerGrp.children.forEach((m) => { m.position.x += ix; m.position.y += iy; m.userData.x0 += ix; m.userData.y0 += iy; });
          const frameTex = spec?.src ? kit.urlTexture(spec.src) : null;
          const frameGrp = kit.crackShards(frameTex, box.w, box.h, `${ctx.seed}:win:${name}`, {
            rays, rings: RINGS,
            material: (tx) => { const m = kit.plain(tx, 1); if (!tx) m.color?.set?.('#3a3e48'); return m; },
          });
          const all = [...shardsOf(innerGrp, 0), ...shardsOf(frameGrp, 1)];
          const sg = add(g, new THREE.Group(), order + 0.1);
          all.forEach((s) => add(sg, s.m, order + 0.1 + s.z * 0.01));
          // ひび（暗い縁の上に白い芯。衝撃点から近い順に走る）
          const { impact } = frameGrp.userData;
          const near = (q) => Math.hypot(q[0] - impact[0], q[1] - impact[1]);
          const cracks = frameGrp.userData.cracks.map((c) => {
            const [a, b] = near(c.a) <= near(c.b) ? [c.a, c.b] : [c.b, c.a];
            const dark = add(g, fill(ctx, '#07070a', 1, 1), order + 0.3);
            const white = add(g, fill(ctx, '#ffffff', 1, 1), order + 0.31);
            return { a, b, dark, white, at: CRACK + c.d * (BREAK - CRACK - 60) };
          });
          const flash = add(g, fill(ctx, '#ffffff', box.w, box.h), order + 0.4);
          pieces.push({ g, all, cracks, flash, box, impact });
        };

        part('portraitFrame', boxes.portrait, {
          tex: kit.imageTexture(portraitImg),
          aspect: portraitImg ? (portraitImg.naturalWidth || portraitImg.width || 1) / (portraitImg.naturalHeight || portraitImg.height || 1) : null,
          color: '#0b0d14',
        }, 20);
        part('barFrame', boxes.bar, { tex: null, color: '#120407' }, 21, 10);   // 細長いので細かく割る

        // 砕けた瞬間の硬い火花（部品の衝撃点から）
        const sparkCols = ['#ffffff', '#ffd54a', '#c9d0da'];
        const sparks = Array.from({ length: BREAK_SPARKS }, (_, i) => {
          const pc = pieces[i % pieces.length];
          const m = add(root, fill(ctx, sparkCols[i % sparkCols.length], 1, 1), 23);
          const a = rand() * Math.PI * 2;
          const sx = right ? -1 : 1;
          return {
            m, a, len: 50 + rand() * 90, reach: 120 + rand() * 200, w: 2 + rand() * 3,
            x: pc.box.x + pc.box.w / 2 + sx * pc.impact[0], y: pc.box.y + pc.box.h / 2 + pc.impact[1],
          };
        });

        /** 線分 a → b（部品の中の座標）を k まで伸ばして置く */
        const lay = (m, a, b, k, wpx) => {
          const dx = (b[0] - a[0]) * k; const dy = (b[1] - a[1]) * k;
          const len = Math.hypot(dx, dy);
          place(m, a[0] + dx / 2, a[1] + dy / 2, { sx: Math.max(0.001, len), sy: wpx, rot: Math.atan2(dy, dx) });
        };

        return {
          root,
          update: ({ localMs: t }) => {
            const u = Math.max(0, (t - BREAK) / 1000);
            const out = 1 - seg(t, FALL_FADE[0], FALL_FADE[1]);
            pieces.forEach((pc) => {
              pc.cracks.forEach((c) => {
                const k = tween(t, c.at, c.at + 50, 'ease_out');
                const on = t >= c.at && t < BREAK;
                lay(c.dark, c.a, c.b, k, 8);
                lay(c.white, c.a, c.b, k, 3);
                fade(c.dark, on && k > 0 ? 0.9 : 0);
                fade(c.white, on && k > 0 ? 1 : 0);
              });
              pc.all.forEach((s) => {
                const x = s.x0 + s.vx * u;
                const y = s.y0 + s.vy * u + 2600 * u * u;
                s.m.position.set(x, y, 0);
                s.m.rotation.z = s.spin * u;
                const sc = 1 - 0.3 * seg(t, BREAK, FALL_FADE[1]);
                s.m.scale.set(sc, sc, 1);
                fade(s.m, t >= BREAK && t < FALL_FADE[1] ? out : 0);
              });
              fade(pc.flash, t >= BREAK && t < BREAK + FLASH ? 0.95 : 0);
            });
            const sk = tween(t, BREAK, BREAK + 220, 'expo_out');
            const thin = 1 - seg(t, BREAK + 60, BREAK + 260);
            sparks.forEach((s) => {
              const r = s.reach * sk;
              const len = s.len * thin;
              place(s.m, s.x + Math.cos(s.a) * (r - len / 2), s.y + Math.sin(s.a) * (r - len / 2), { sx: Math.max(0.001, len), sy: s.w, rot: s.a });
              fade(s.m, t >= BREAK && thin > 0.02 ? 1 : 0);
            });
          },
        };
      },
    },
    {
      id: 'plate',
      label: '勝者の鋼の板（デッキのイラスト・アイコン・名前・デッキ名）',
      space: 'screen',
      script: (ctx) => {
        const { THREE } = ctx;
        const { w: W, h: H } = canvasOf(ctx);
        const root = new THREE.Group();
        const w = winnerOf(ctx.payload);
        if (w == null) return { root };
        const cx = W / 2;
        const side = w === 0 ? -1 : 1;   // 勝者の側（板が来る側）
        const rand = seeded(`${ctx.seed}:win:plate`);
        const groups = {};
        const layer = (n) => { if (!groups[n]) groups[n] = add(root, new THREE.Group(), n); return groups[n]; };

        const { g: plate } = duelistPlate(ctx, root, w, { order: 4 });

        // 速度線（板の後ろの水平の硬い線）
        const lines = Array.from({ length: LINES }, (_, i) => {
          const m = add(layer(3), fill(ctx, i % 3 === 0 ? '#ffffff' : '#c9d0da', 1, 1), 3);
          return { m, dy: (rand() - 0.5) * PH * 0.9, len: 260 + rand() * 360, lag: 40 + rand() * 260, w: 2 + rand() * 3 };
        });

        // 急停止: 前の縁（進む向きの斜めの辺）の閃光と火花、画面の閃光 1 フレーム
        const seamAngle = Math.atan2(PH, -SKEW);
        const lead = -side;   // 進む向き
        const edgeX = cx + lead * (PW / 2 - SKEW / 2);
        const seam = add(layer(9), fill(ctx, '#ffffff', 1, 1), 9);
        const flash = add(layer(12), place(fill(ctx, '#ffffff', W, H, { blend: 'add' }), cx, H / 2), 12);
        const sparkCols = ['#ffffff', '#ffd54a', '#ffb020', '#fff2b0'];
        const sparks = Array.from({ length: 24 }, (_, i) => {
          const m = add(layer(8), fill(ctx, sparkCols[i % sparkCols.length], 1, 1), 8);
          return { m, a: (lead < 0 ? Math.PI : 0) + (rand() - 0.5) * 1.8, along: (rand() - 0.5) * PH, len: 100 + rand() * 200, w: 3 + rand() * 4, speed: 900 + rand() * 1400 };
        });

        /** ヒットストップの時間の写し（板が止まった後は PLATE_STOP だけ遅れる） */
        const hs = (t) => (t < PLATE_HIT ? t : (t < PLATE_HIT + PLATE_STOP ? PLATE_HIT : t - PLATE_STOP));

        return {
          root,
          update: ({ localMs: t }) => {
            const u = hs(t);
            const sq = tween(t, OUT[0], OUT[1], 'power2_in');
            const tremble = t >= PLATE_HIT + PLATE_STOP && t < OUT[0] ? Math.sin(t / 37) * 1.5 : 0;
            // 勝者の側から加速して中央へ → 急停止 → 見得 → 縦に潰れて消える
            const inK = tween(t, PLATE_IN, PLATE_HIT, 'power3_in');
            const from = cx + side * (W / 2 + PW);
            const x = from + (cx - from) * inK + tremble;
            place(plate, x, PLATE_Y, { sx: 1 + 0.12 * sq, sy: Math.max(0.001, 1 - sq) });
            fade(plate, t >= PLATE_IN && t < OUT[1] ? 1 : 0);

            lines.forEach((l) => {
              const k = seg(t, PLATE_IN + 60, PLATE_HIT + 40);
              const on = t >= PLATE_IN + 60 && t < PLATE_HIT + 40;
              const len = l.len * (1 - k * 0.6);
              place(l.m, x + side * (PW / 2 + l.lag + len / 2), PLATE_Y + l.dy, { sx: Math.max(0.001, len), sy: l.w });
              fade(l.m, on ? 0.9 : 0);
            });

            place(seam, edgeX, PLATE_Y, { sx: Math.hypot(PH, SKEW) * 1.06, sy: 9, rot: seamAngle });
            fade(seam, t >= PLATE_HIT && t < PLATE_HIT + FLASH ? 1 : 0);
            fade(flash, t >= PLATE_HIT && t < PLATE_HIT + 17 ? 0.14 : 0);

            const since = Math.max(0, (u - PLATE_HIT) / 1000);
            const ca = Math.cos(seamAngle); const sa = Math.sin(seamAngle);
            sparks.forEach((s) => {
              const k = Math.min(1, since / 0.42);
              const ox = edgeX + ca * s.along; const oy = PLATE_Y + sa * s.along;
              const r = s.speed * since * (1 - 0.4 * k);
              const len = s.len * (1 - k);
              place(s.m, ox + Math.cos(s.a) * (r + len / 2), oy + Math.sin(s.a) * (r + len / 2), { sx: Math.max(0.001, len), sy: s.w, rot: s.a });
              fade(s.m, t >= PLATE_HIT && k < 1 ? 1 : 0);
            });
          },
        };
      },
    },
    {
      id: 'winner',
      label: '勝者の名乗り（板の上の「WINNER 名前!」の 1 行の見出し）',
      space: 'screen',
      script: (ctx) => {
        const { THREE, kit } = ctx;
        const { w: W, h: H } = canvasOf(ctx);
        const root = new THREE.Group();
        const w = winnerOf(ctx.payload);
        if (w == null) return { root };
        const cx = W / 2;
        const rand = seeded(`${ctx.seed}:win:headline`);
        const groups = {};
        const layer = (n) => { if (!groups[n]) groups[n] = add(root, new THREE.Group(), n); return groups[n]; };

        /*
         * 見出しは 2 枚の金属文字（「WINNER」と「名前!」）を 1 行に並べる。名前の後ろにだけ、その人の色の斬り傷の差し
         * （damage の数字と同じ太い線・細い線・黒の影。色の縁や発光にはしない）
         */
        const color = playerColor(ctx, w);
        const head = kit.metalTextTexture('WINNER', { size: WIN_SIZE, edge: null });
        const name = kit.metalTextTexture(`${duelistName(ctx, w)}!`, { size: WIN_SIZE, edge: null });
        const gw = (t) => lifeNumberGlyphWidth(t, WIN_SIZE);
        const gap = WIN_SIZE * 0.32;
        const total = gw(head) + gap + gw(name);
        // 画面の幅の WIN_FIT。長い名前で縮めても文字は 46px を割らない
        const fit = Math.max(46 / WIN_SIZE, Math.min(1.25, (W * WIN_FIT) / Math.max(1, total)));
        const line = add(root, new THREE.Group(), 10);
        const headX = -total / 2 + gw(head) / 2;
        const nameX = total / 2 - gw(name) / 2;
        putQuad(ctx, line, head, headX, 10.3);
        const nh = name.height;
        const bar = (c) => { const m = fill(ctx, c, 1, 1); return m; };
        const slashes = [
          { m: bar(color), y: nh * 0.16, w: gw(name) * 1.08, h: nh * 0.11, dx: 0 },
          { m: bar('#ffffff'), y: nh * 0.26, w: gw(name) * 0.7, h: nh * 0.035, dx: 0 },
          { m: bar('#050506'), y: nh * 0.16 + 8, w: gw(name) * 1.08, h: nh * 0.11, dx: 12 },
        ];
        slashes.forEach((sl, i) => { sl.m.renderOrder = [10.1, 10.12, 10.05][i]; line.add(sl.m); });
        putQuad(ctx, line, name, nameX, 10.3);

        // 叩きつけの閃光（1 フレーム）と硬い火花（見出しの両端から外へ）
        const flash = add(layer(12), place(fill(ctx, '#ffffff', W, H, { blend: 'add' }), cx, H / 2), 12);
        const sparkCols = ['#ffffff', '#ffd54a', '#ffb020', '#fff2b0'];
        const sparks = Array.from({ length: WIN_SPARKS }, (_, i) => {
          const m = add(layer(9), fill(ctx, sparkCols[i % sparkCols.length], 1, 1), 9);
          const side = i % 2 === 0 ? -1 : 1;
          return {
            m, side, a: (side < 0 ? Math.PI : 0) + (rand() - 0.5) * 1.4, dy: (rand() - 0.5) * WIN_SIZE * fit,
            len: 120 + rand() * 200, w: 3 + rand() * 4, speed: 900 + rand() * 1300,
          };
        });
        const edge = (total * fit) / 2;

        return {
          root,
          update: ({ localMs: t }) => {
            const sq = tween(t, OUT[0], OUT[1], 'power2_in');
            const tremble = t >= WIN_HIT + WIN_STOP && t < OUT[0] ? Math.sin(t / 37) * 1.5 : 0;
            // 2.6 倍から叩きつけ → 止め → 小さく弾んで止まる → 縦に細く潰れて消える
            const slam = tween(t, WIN_IN, WIN_HIT, 'power2_in');
            const after = WIN_HIT + WIN_STOP;
            const settle = t >= after ? 1 + 0.05 * Math.sin(seg(t, after, after + 180) * Math.PI) : 1;
            const s = (2.6 - 1.6 * slam) * settle * fit;
            const jitter = t >= WIN_HIT && t < after ? Math.sin(t / 7) * 3 : 0;
            place(line, cx + tremble + jitter, WIN_Y, { sx: s * (1 + 0.15 * sq), sy: s * Math.max(0.001, 1 - sq) });
            fade(line, t >= WIN_IN && t < OUT[1] ? seg(t, WIN_IN, WIN_IN + 15) : 0);
            // 差しは叩きつけで伸びる
            const k = t < WIN_HIT ? 0.3 : 0.3 + 0.7 * tween(t, WIN_HIT, WIN_HIT + 70, 'expo_out');
            slashes.forEach((sl) => place(sl.m, nameX + sl.dx, sl.y, { sx: Math.max(0.001, sl.w * k), sy: sl.h, rot: -0.1 }));

            fade(flash, t >= WIN_HIT && t < WIN_HIT + 17 ? 0.16 : 0);
            const since = t < after ? 0 : (t - after) / 1000;
            sparks.forEach((sp) => {
              const kk = Math.min(1, since / 0.4);
              const r = sp.speed * since * (1 - 0.4 * kk);
              const len = sp.len * (1 - kk);
              const ox = cx + sp.side * edge; const oy = WIN_Y + sp.dy;
              place(sp.m, ox + Math.cos(sp.a) * (r + len / 2), oy + Math.sin(sp.a) * (r + len / 2), { sx: Math.max(0.001, len), sy: sp.w, rot: sp.a });
              fade(sp.m, t >= WIN_HIT && kk < 1 ? 1 : 0);
            });
          },
        };
      },
    },
  ],
}, 'win_effect.js');
